import React, { useContext, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { format } from 'date-fns';
import Container from "../../Container/Container";
import SearchComponent from "../../SearchComponent/SearchComponent";
import TableComponent from "../../TableComponent/TableComponent";
import NoData from "../../NoData/NoData";
import ToastComponent from "../../ToastComponent";
import CreateProject from "./CreateProject/CreateProject";
import EditProject from "./CreateProject/EditProject";
import SettingsDelete from "../SettingsDelete/SettingsDelete";
import { getTableColumns, capitalize } from "../../../util";
import './Projects.css';

// API & Authentication
import { fetchProjects } from "../../../apis/projects.org";
import { AuthContext } from "../../../AuthContext";
import usePermissions from "../../../hooks/userPermissions";
import ProjectSettingsPage from "./CreateProject/ProjectSettingsPage";

function Projects() {
  const { canProjectCreate, canProjectUpdate, canProjectDelete } = usePermissions();
  const { authData } = useContext(AuthContext);
  const { access_token, token_type } = authData;
  const [searchResults, setSearchResults] = useState([])
  const [originalProjectsData, setOriginalProjectsData] = useState([])
  const [projectsRows, setProjectsRows] = useState([])
  const [projectsColumns, setProjectsColumns] = useState([])
  const [selectedProj, setSelectedProj] = useState(null);
  const [openDeleteProjects, setOpenDeleteProjects] = useState(false);
  const [openCreateProject, setOpenCreateProject] = useState(false);
  const [openEditProject, setOpenEditProject] = useState(false);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState(0);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  // create rows Data
  const createRowsData = (data) => {
    return data?.map(item => {
      if (!item?.is_deleted) {
        return {
          'Org ID': item?.org_id,
          'Project ID': item?.project_id,
          'Project Name': item?.name,
          'Client': capitalize(item?.client),
        }
      }
      return null;
    }).filter(item => item !== null);
  };
  // create createColumns Data 
  const createColumnsData = (rowsData) => {
    return [...getTableColumns(rowsData), {
      "field": "action",
      "header": "Action"
    }];
  };

  useEffect(() => {
    let settime;
    if (isToastOpen) {
      settime = setTimeout(() => {
        setIsToastOpen(false);
      }, 4000)
    }

    return () => clearTimeout(settime);
  }, [isToastOpen])

  const { data: projectsData, isLoading: loadingProjects, isFetching, refetch } = useQuery({
    queryKey: ['projects', {
      access_token,
      token_type
    }],
    queryFn: fetchProjects,
    refetchOnWindowFocus: false
  })

  useEffect(()=>{
      if (projectsData && projectsData.length > 0) {
        setSearchResults(projectsData);
        setOriginalProjectsData(projectsData);
        const rowsData = createRowsData(projectsData);
        const columnsData = createColumnsData(rowsData);
        setProjectsRows(rowsData);
        setProjectsColumns(columnsData);
      }
  },[projectsData]);

  // Organization serach functionality
  const handleSearchFunctionality = (event) => {
    let resultArray = [];
    const query = event?.toLowerCase();
    if (!query) {
      resultArray = originalProjectsData;
      setSearchResults(originalProjectsData)

    } else {
      resultArray = originalProjectsData?.filter(
        (data) => {
          return data?.name?.toLowerCase().includes(query.toLowerCase())
            || data?.client?.toLowerCase().includes(query.toLowerCase());
        }
      );
      setSearchResults(resultArray);
    }

    const rowsData = createRowsData(resultArray);
    const columnsData = createColumnsData(rowsData);
    setProjectsRows(rowsData);
    setProjectsColumns(columnsData);

  }

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1, height: '130vh !important' }}
        open={ loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container>
        <div className="projectsContainer">
          <div className="projectsHeader">
            <h2>Your Projects</h2>
          </div>
          <SearchComponent
            placeholder="Search Projects"
            // onSearch={(query) => console.log(query)}
            CreateButtonTitle="Create Project"
            setOpenCreateSettings={setOpenCreateProject}
            canCreate={canProjectCreate}
            onSearch={handleSearchFunctionality}
          />
          {(searchResults && projectsColumns.length > 0 && projectsRows.length > 0) &&
            <TableComponent
              columns={projectsColumns}
              rows={projectsRows}
              setSelected={setSelectedProj}
              setOpenEditSettings={setOpenEditProject}
              setOpenDeleteSettings={setOpenDeleteProjects}
              setOpenSettingsModal={setOpenSettingsModal}
              canUpdate={canProjectUpdate}
              canDelete={canProjectDelete}
              isProjectTabActive

            />
          }
          {searchResults?.length === 0 && <NoData main={'Projects'} />}
          {openCreateProject &&
            <CreateProject
              openCreateProject={openCreateProject}
              setOpenCreateProject={setOpenCreateProject}
              setIsToastOpen={setIsToastOpen}
              setToastMessage={setToastMessage}
              setLoading={setLoading}
              setErrorCode={setErrorCode}
            />
          }
          {openEditProject &&
            <EditProject
              openEditProject={openEditProject}
              setOpenEditProject={setOpenEditProject}
              selectedProj={selectedProj}
              setIsToastOpen={setIsToastOpen}
              setToastMessage={setToastMessage}
              setLoading={setLoading}
              setErrorCode={setErrorCode}
            />
          }
          {openDeleteProjects &&
            <SettingsDelete
              openDeleteSettings={openDeleteProjects}
              setOpenDeleteSettings={setOpenDeleteProjects}
              title={'Project'}
              mainTitle={selectedProj["Project Name"] || 'Project'}
              selected={selectedProj}
              setIsToastOpen={setIsToastOpen}
              setToastMessage={setToastMessage}
              setLoading={setLoading}
              setErrorCode={setErrorCode}
            />
          }
          {
            openSettingsModal && (
              <ProjectSettingsPage
                openSettingsModal={openSettingsModal}
                setOpenSettingsModal={setOpenSettingsModal}
                setIsToastOpen={setIsToastOpen}
                setToastMessage={setToastMessage}
                setLoading={setLoading}
                selectedProj={selectedProj}
                setErrorCode={setErrorCode} />
            )
          }
          {isToastOpen &&
            <ToastComponent
              isToastOpen={isToastOpen}
              setIsToastOpen={setIsToastOpen}
              message={toastMessage}
              errorCode={errorCode}
            />
          }
        </div>
      </Container>
    </>
  );
}

export default Projects;